import React, {
  RefObject,
} from 'react';
import clsx from 'clsx';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  QueryObserverResult,
} from '@tanstack/react-query';
import ServiceToolbar, {
  ServiceToolbarAction,
} from 'views/EMR/components/ServiceLookup/ServiceToolbar';
import {
  ServiceType,
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  FilterOptionsState,
} from '@mui/material/useAutocomplete';
import Lookup from 'views/EMR/components/ServiceLookup/Lookup';
import ServiceList from 'views/EMR/components/ServiceLookup/List';
import ServiceProvider from 'views/EMR/components/ServiceLookup/ServiceContext';
import {
  useUpdateBillingCode,
} from 'views/EMR/components/ServiceLookup/hooks';
import Label from 'pages/Dashboard/components/Label';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type {
  ClaimAuthorizationOption,
} from 'views/EMR/components/Authorizations/utils';
import {
  VisitNoteBillingAssessmentCodeDTO,
} from 'dtos/visitNoteBillingAssessmentCodeDTO';

type Part = 'root' | 'wrapper' | 'container' | 'table' | 'label' | 'cell' | 'list';

const RECOMMENDED_ICD_CODE_MAX = 12;

export type ExternalFilter = (
  options: VisitNoteServiceCode[],
  state: FilterOptionsState<VisitNoteServiceCode>,
) => VisitNoteServiceCode[];

export type ServicesLookupProps = {
  id: string;
  className?: string;
  classes?: Partial<Record<Part, string>>;
  dropdownId: string;
  placeholder?: string;
  value?: VisitNoteServiceCode[];
  normalizeOption: (option: VisitNoteServiceCode) => VisitNoteServiceCode;
  onChange: (val?: VisitNoteServiceCode[]) => void;
  visitNoteId?: number;
  saveVisitNote?: () => Promise<void>;
  patientId?: number;
  favorites?: VisitNoteServiceCode[];
  refetchFavoriteCode?: () => Promise<QueryObserverResult>;
  type?: ServiceType;
  label?: string;
  isClaim?: boolean;
  isAuthorizationAllowance?: boolean;
  claimAuthorizationOptions?: ClaimAuthorizationOption[];
  allowDuplicate?: boolean;
  actions?: ServiceToolbarAction[];
  externalFilter?: ExternalFilter;
  readonly?: boolean;
  onSalt?: () => void;
  formKeys?: Record<string, string>;
  required?: boolean;
  error?: boolean;
  showCustomServiceCodePrice?: boolean;
  hideLabel?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  /**
   * Set to false when rendering outside a React Hook Form FormProvider.
   * Disables form-coupled side-effects (billing-code sync, AI toolbar actions).
   * Defaults to true to preserve backward compatibility.
   */
  formEnabled?: boolean;
  /**
   * Assessment codes to display in the Linked ICD Codes column for CPT code rows.
   * In form mode, derive this with useWatch and pass it here.
   * In standalone/read-only mode, pass the codes directly or omit for an empty list.
   */
  assessmentCodes?: VisitNoteBillingAssessmentCodeDTO[];
};

type FormSyncProps = {
  type: ServiceType;
  formKeys: Record<string, string>;
};

function FormSync({ type, formKeys }: FormSyncProps) {
  useUpdateBillingCode(type, formKeys);
  return null;
}

export default function ServicesLookup(props: ServicesLookupProps) {
  const {
    id,
    value: selectedServices = [],
    label = 'Custom Services',
    classes,
    isClaim = false,
    isAuthorizationAllowance = false,
    readonly = false,
    className,
    formKeys,
    required,
    type = 'CustomCode',
    error,
    hideLabel = false,
    containerRef,
    formEnabled = true,
  } = props;

  const showLabel = (!readonly || (readonly && selectedServices?.length > 0)) && !hideLabel;

  return (
    <ServiceProvider key={`${type}-${id}`} {...props}>
      {formEnabled && <FormSync type={type} formKeys={formKeys ?? {}} />}
      <div ref={containerRef} className={clsx('flex flex-col gap-4', className)}>
        {showLabel && (
          <div className={clsx('flex flex-col gap-0.5', classes?.root)}>
            <Wrapper
              id={id}
              required={required}
              error={error}
              showErrorText={false}
              className={clsx('w-100', classes?.wrapper)}
              label={(
                <div className="flex justify-between font-semibold items-center">
                  {(!readonly || (readonly && selectedServices?.length > 0)) && !hideLabel && (
                  <Label
                    className={classes?.label}
                    id={id}
                    label={label}
                    required={required}
                  />
                  )}
                  {!readonly && <ServiceToolbar />}
                </div>
          )}
              containerClassName={clsx('!block rounded', classes?.container)}
            >
              {!readonly && <Lookup />}
            </Wrapper>
            {!readonly && type === 'ICDCode' && selectedServices.length > RECOMMENDED_ICD_CODE_MAX && (
              <div className="whitespace-nowrap flex items-center gap-1 rounded px-1 py-0.5 text-xs font-semibold text-amber-700">
                <WarningAmberRoundedIcon className="!h-4 !w-4 shrink-0 text-amber-700" />
                {selectedServices.length} Selected. Consider limiting selections to {RECOMMENDED_ICD_CODE_MAX} codes.
              </div>
            )}
          </div>
        )}
        {selectedServices?.length > 0 && (
          <ServiceList
            className={classes?.table}
            classes={{
              body: classes?.list,
            }}
            isClaim={isClaim}
            isAuthorizationAllowance={isAuthorizationAllowance}
          />
        )}
      </div>
    </ServiceProvider>
  );
}
